import { useContext } from "react";
import { UNSAFE_LocationContext } from "react-router";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams, useSearchParams, type Location } from "react-router-dom";
import { SettingsUrlSync } from "@/shell/settings-url";
import { AppShell } from "@/shell/AppShell";
import { FluentThemeRoot } from "@/shell/FluentThemeRoot";
import { CreatePlanRoute } from "@/features/plans/CreatePlanDialog";
import { DesignSystemPage } from "@/pages/DesignSystemPage";
import { GlobalResourcesPage } from "@/pages/GlobalResourcesPage";
import { MembersPage } from "@/pages/MembersPage";
import { MemberProfilePage } from "@/pages/MemberProfilePage";
import { MyTasksPage } from "@/pages/MyTasksPage";
import { McpConnectionPage } from "@/pages/McpConnectionPage";
import { SystemLogsPage } from "@/pages/SystemLogsPage";
import { ActivityPage } from "@/features/activity/ActivityPage";
import { HomePage } from "@/pages/HomePage";
import { PlansPage } from "@/pages/PlansPage";
import { PortfolioPage } from "@/pages/PortfolioPage";
import { ProjectBudgetPage } from "@/pages/ProjectBudgetPage";
import { ProjectCalendarPage } from "@/pages/ProjectCalendarPage";
import { ProjectFilesPage } from "@/pages/ProjectFilesPage";
import { WorkspaceDocsPage } from "@/pages/WorkspaceDocsPage";
import { WorkspaceFilesPage } from "@/pages/WorkspaceFilesPage";
import { ProjectLayout } from "@/pages/ProjectLayout";
import { ProjectDocsPage } from "@/pages/ProjectDocsPage";
import { ProjectIntakePage } from "@/pages/ProjectIntakePage";
import { ProjectRecordsPage } from "@/pages/ProjectRecordsPage";
import { ProjectReportsPage } from "@/pages/ProjectReportsPage";
import { ProjectSchedulePage } from "@/pages/ProjectSchedulePage";
import { ProjectTasksPage } from "@/pages/ProjectTasksPage";
import { ProjectStakeholdersPage } from "@/pages/ProjectStakeholdersPage";
import { ProjectTeamPage } from "@/pages/ProjectTeamPage";
import { ProjectWeeklyReportPage } from "@/pages/ProjectWeeklyReportPage";
import { ProjectReviewPage } from "@/pages/ProjectReviewPage";
import { InviteAcceptPage } from "@/features/access/InviteAcceptPage";
import { LoginPage } from "@/features/access/LoginPage";
import { InviteLinkPage } from "@/features/access/InviteLinkPage";
import { ShareViewPage } from "@/features/access/ShareViewPage";

/** Legacy /board tab → 작업 보드 뷰. Preserves linked/by filters. */
function RedirectBoardToTasks() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  if (next.get("by") && !next.get("group")) {
    next.set("group", next.get("by")!);
    next.delete("by");
  }
  next.set("view", "board");
  return <Navigate to={`/plans/${id}/tasks?${next.toString()}`} replace />;
}

/** 옛 /overview(요약)는 현황으로 합쳐졌다. */
function RedirectOverviewToStatus() {
  const { id } = useParams();
  return <Navigate to={`/plans/${id}/reports`} replace />;
}

/** 옛 /analytics는 작업 화면의 분석 보기로 옮겨졌다. */
function RedirectAnalyticsToTasks() {
  const { id } = useParams();
  return <Navigate to={`/plans/${id}/tasks?view=analytics`} replace />;
}

/** 없는 탭은 현황으로 보낸다. 플랜이 없으면 레이아웃이 같은 안내를 보여 이 이동은 일어나지 않는다. */
function RedirectUnknownTab() {
  const { id } = useParams();
  return <Navigate to={`/plans/${id}/reports`} replace />;
}

/** 옛 `/projects/:id` 는 `/plans/:id` 와 같은 화면이다. */
function RedirectLegacyPlan() {
  const { id, "*": rest } = useParams();
  const [params] = useSearchParams();
  const tail = rest ? rest : "schedule";
  const qs = params.toString();
  return <Navigate to={`/plans/${id}/${tail}${qs ? `?${qs}` : ""}`} replace />;
}

/** /plans 북마크·링크를 플랜 홈(/)으로 모읍니다. 쿼리는 그대로 둡니다. */
function RedirectPlansHome() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={qs ? `/?${qs}` : "/"} replace />;
}

export function AppRoutes() {
  const location = useLocation();
  const routeLocation = useContext(UNSAFE_LocationContext);
  const background = (location.state as { background?: Location } | null)?.background;
  const keepPage = location.pathname.startsWith("/settings") ? background : undefined;
  const routes = (
    <Routes location={keepPage ?? location}>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/s/:token" element={<ShareViewPage />} />
      <Route path="/invite/t/:token" element={<InviteLinkPage />} />
      <Route path="/i/:token" element={<InviteAcceptPage />} />
      <Route element={<AppShell />}>
        <Route path="/" element={<PlansPage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/plans" element={<RedirectPlansHome />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/tasks" element={<MyTasksPage />} />
        <Route path="/activity" element={<ActivityPage withDrawers />} />
        <Route path="/docs" element={<WorkspaceDocsPage />} />
        <Route path="/files" element={<WorkspaceFilesPage />} />
        <Route path="/resources" element={<GlobalResourcesPage />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/members/:personId" element={<MemberProfilePage />} />
        <Route path="/teams/:teamId" element={<Navigate to="/members?view=teams" replace />} />
        <Route path="/design-system" element={<DesignSystemPage />} />
        <Route path="/system/logs" element={<SystemLogsPage />} />
        <Route path="/system/mcp" element={<McpConnectionPage />} />
        <Route path="/settings" element={<Navigate to="/settings/app/appearance" replace />} />
        <Route path="/settings/:scope/:section?" element={null} />
        <Route path="/projects/new" element={<CreatePlanRoute />} />
        <Route path="/projects/:id" element={<RedirectLegacyPlan />} />
        <Route path="/projects/:id/*" element={<RedirectLegacyPlan />} />
        <Route path="/plans/:id" element={<ProjectLayout />}>
          <Route index element={<Navigate to="schedule" replace />} />
          <Route path="overview" element={<RedirectOverviewToStatus />} />
          <Route path="status" element={<ProjectReportsPage />} />
          <Route path="schedule" element={<ProjectSchedulePage />} />
          <Route path="tasks" element={<ProjectTasksPage />} />
          <Route path="board" element={<RedirectBoardToTasks />} />
          <Route path="intake" element={<ProjectIntakePage />} />
          <Route path="records" element={<ProjectRecordsPage />} />
          <Route path="docs" element={<ProjectDocsPage />} />
          <Route path="docs/:docId" element={<ProjectDocsPage />} />
          <Route path="files" element={<ProjectFilesPage />} />
          <Route path="calendar" element={<ProjectCalendarPage />} />
          <Route path="team" element={<ProjectTeamPage />} />
          <Route path="stakeholders" element={<ProjectStakeholdersPage />} />
          <Route path="budget" element={<ProjectBudgetPage />} />
          <Route path="weekly" element={<ProjectWeeklyReportPage />} />
          <Route path="review" element={<ProjectReviewPage />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route path="analytics" element={<RedirectAnalyticsToTasks />} />
          <Route path="reports" element={<ProjectReportsPage />} />
          <Route path="*" element={<RedirectUnknownTab />} />
        </Route>
      </Route>
    </Routes>
  );
  if (!keepPage) return routes;
  return (
    <UNSAFE_LocationContext.Provider value={{ location: keepPage, navigationType: routeLocation.navigationType }}>
      {routes}
    </UNSAFE_LocationContext.Provider>
  );
}

export function App() {
  return (
    <FluentThemeRoot>
      <BrowserRouter>
        <SettingsUrlSync />
        <AppRoutes />
      </BrowserRouter>
    </FluentThemeRoot>
  );
}
